import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useStudentView } from '../contexts/StudentViewContext';
import { authFetch } from '../utils/api';
import { gradeClass, gradeColor } from '../utils/grades';
import { formatDate } from '../utils/dates';
import { renderSkillIcon } from '../utils/geo';
import { StudentBanner } from '../components/cfi/StudentBanner';
import { StudentPicker } from '../components/cfi/StudentPicker';

// ── Score → state color ──────────────────────────────────────────────────────
function scoreColor(score) {
  if (score == null) return 'var(--text-muted)';
  if (score >= 80) return '#065f46';
  if (score >= 50) return '#1e3a8a';
  return '#713f12';
}
function scoreBg(score) {
  if (score == null) return 'rgba(117,143,168,0.15)';
  if (score >= 80) return '#bbf7e0';
  if (score >= 50) return '#bfdbfe';
  return '#fef08a';
}
function scoreBorder(score) {
  if (score == null) return 'rgba(117,143,168,0.35)';
  if (score >= 80) return '#10b981';
  if (score >= 50) return '#3b82f6';
  return '#ca8a04';
}

// ── Skill helpers ────────────────────────────────────────────────────────────
const SKILL_ICON_MAP = {
  'Crosswind': '🌬️', 'Gust': '💨', 'Windshear': '⚡', 'Night Flight': '🌙',
  'IFR': '🧭', 'LIFR': '🔴', 'Short Field': '🛬', 'High Altitude Ops': '🏔️',
};
function skillIcon(name) { return renderSkillIcon(SKILL_ICON_MAP[name] || '🎯'); }

// ── Shared tile components ───────────────────────────────────────────────────
function ScoreBadge({ value, label, score }) {
  return (
    <div style={{
      width: 70, height: 70, borderRadius: 16, flexShrink: 0,
      background: scoreBg(score),
      border: `1.5px solid ${scoreBorder(score)}`,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    }}>
      <span style={{ fontSize: 22, fontWeight: 700, color: scoreColor(score), fontFamily: 'var(--font-mono)', lineHeight: 1.1 }}>{value}</span>
      <span style={{ fontSize: 11, fontWeight: 600, color: scoreColor(score), textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.75, marginTop: 2 }}>{label}</span>
    </div>
  );
}

function TileChevron({ open }) {
  return (
    <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0, marginTop: 2, transition: 'transform 0.18s', display: 'inline-block', transform: open ? 'rotate(180deg)' : 'none' }}>▾</span>
  );
}

function TileDivider() {
  return <div style={{ borderTop: '1px solid var(--border)' }} />;
}

// FAA WINGS section row — used inside the expanded WINGS tile body.
// One section per status (assigned / registered / completed).
function WingsSection({ title, meta, iconColor, icon, items, first }) {
  const headerStyle = first
    ? { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }
    : { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8, marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)' };
  return (
    <div>
      <div style={headerStyle}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</span>
        <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{meta}</span>
      </div>
      {items.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic', padding: '4px 0' }}>None</div>
      ) : items.map((it, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '6px 0' }}>
          <span style={{ fontSize: 14, color: iconColor, marginTop: 1, flexShrink: 0, width: 16, textAlign: 'center' }}>{icon}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.35 }}>{it.title}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{it.meta}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Mock data for the WINGS tile until the FAASafety.gov sync is wired up.
// Replace with API-fed values once `/api/user/wings` exists.
const MOCK_WINGS = {
  assigned: [
    { title: 'Crosswind landing techniques', meta: 'CFI J. Smith \u00B7 assigned 12 May 2026' },
    { title: 'Basic 1, phase 14 \u2014 night operations focus', meta: 'Heritage Flight Academy \u00B7 due 30 Jun 2026' },
  ],
  registered: [
    { title: 'ALC-58 \u2014 spatial disorientation', meta: 'Online course \u00B7 self-paced \u00B7 1 knowledge credit' },
    { title: 'Mountain flying refresher', meta: 'Webinar \u00B7 18 May 2026 19:00 EDT \u00B7 1 knowledge credit' },
  ],
  completed: [
    { title: 'Basic 1, phase 12', meta: 'Aug 2025 \u00B7 flight review valid through Aug 2026' },
    { title: 'ALC-25 \u2014 aeronautical decision making', meta: '2 Mar 2026 \u00B7 1 knowledge credit' },
    { title: 'Crosswind landing techniques', meta: '14 Apr 2026 \u00B7 1 flight credit' },
  ],
};

function InlineBucket({ label, value, displayVal, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ fontSize: 10, color: 'var(--text-muted)', width: 52, flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, height: 3, borderRadius: 2, background: 'var(--bg-body)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${Math.min(100, value || 0)}%`, background: color, opacity: 0.9, borderRadius: 2 }} />
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, fontFamily: 'var(--font-mono)', width: 42, textAlign: 'right', color, flexShrink: 0 }}>{displayVal}</div>
    </div>
  );
}

function MiniGradeBars({ dist }) {
  const grades = ['A','B','C','D','F'];
  const colors = { A: '#10b981', B: 'var(--accent)', C: '#fcd34d', D: '#f97316', F: '#ef4444' };
  const max = Math.max(...grades.map(g => dist[g] || 0), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 28, flexShrink: 0 }}>
      {grades.map(g => (
        <div key={g} style={{
          width: 8, borderRadius: '2px 2px 0 0', opacity: 0.55,
          height: Math.max(2, Math.round(((dist[g] || 0) / max) * 28)),
          background: colors[g],
        }} />
      ))}
    </div>
  );
}

// ── Explainer sheet (shared) ─────────────────────────────────────────────────
function ExplainerSheet({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.65)' }} onClick={onClose} />
      <div style={{ position: 'relative', background: 'var(--bg-card)', borderRadius: '20px 20px 0 0', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 -8px 40px rgba(0,0,0,0.5)', animation: 'slideUp 0.22s ease-out' }}>
        <div style={{ position: 'sticky', top: 0, background: 'var(--bg-card)', padding: '14px 20px 12px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ width: 36, height: 4, background: '#555', borderRadius: 2, position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)' }} />
          <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary)', marginTop: 8 }}>{title}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 22, cursor: 'pointer', marginTop: 8 }}>×</button>
        </div>
        <div style={{ padding: '20px 20px 60px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
function DashboardPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { viewingStudent, myStudents } = useStudentView();

  const [openTile, setOpenTile] = useState(null); // 'prof'|'flights'|'skills'
  const toggleTile = (name) => setOpenTile(o => o === name ? null : name);

  const [pilotLevel, setPilotLevel] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashFlights, setDashFlights] = useState([]);
  const [dashHasMore, setDashHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showStudentPicker, setShowStudentPicker] = useState(false);
  const [showProfExplainer, setShowProfExplainer] = useState(false);
  const [showLandingExplainer, setShowLandingExplainer] = useState(false);
  const [showSkillsExplainer, setShowSkillsExplainer] = useState(false);

  useEffect(() => { if (!user) { navigate('/login'); } }, [user, navigate]);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    const url = viewingStudent
      ? `/api/user/dashboard?limit=50&as_student=${viewingStudent.user_id}`
      : '/api/user/dashboard?limit=50';
    authFetch(url, token).then(d => {
      setData(d);
      setDashFlights(d.flights || []);
      setDashHasMore((d.flights || []).length < d.flights_total);
    }).catch(console.error).finally(() => setLoading(false));
  }, [viewingStudent, token]);

  useEffect(() => {
    if (!token) return;
    const url = viewingStudent
      ? `/api/pwa/pilot-level?as_student=${viewingStudent.user_id}`
      : '/api/pwa/pilot-level';
    authFetch(url, token)
      .then(d => { if (d && d.level) setPilotLevel(d); })
      .catch(() => {});
  }, [token, viewingStudent]);

  const loadMoreFlights = () => {
    setLoadingMore(true);
    authFetch(`/api/user/dashboard?offset=${dashFlights.length}&limit=50`, token).then(d => {
      setDashFlights(prev => [...prev, ...(d.flights || [])]);
      setDashHasMore(dashFlights.length + (d.flights || []).length < d.flights_total);
    }).catch(console.error).finally(() => setLoadingMore(false));
  };

  if (loading) return <div className="loading"><div className="spinner" /><span>Loading dashboard...</span></div>;
  if (!data) return <div className="empty-state"><p>Could not load dashboard</p></div>;

  const { stats, skills_grid, minimums } = data;
  const pl = pilotLevel;
  const flights = dashFlights;
  const displayName = viewingStudent
    ? (viewingStudent.display_name || viewingStudent.username)
    : (user?.display_name || user?.username);

  // Score state for prof composite
  const profScore = pl?.composite ?? null;
  const profColor = scoreColor(profScore);
  const _hasWeights = pl?.weights && Object.keys(pl.weights).length > 0;
  const showBucket = (k) => !_hasWeights || ((pl.weights?.[k] || 0) > 0);
  // Shared skills calc — same array drives the proficiency Skills bucket AND the Skills Currency tile
  const skb = (pl?.skills_breakdown || []).filter(b => (b.weight || 0) > 0);
  const skbCounts = skb.length ? {
    current: skb.filter(b => b.status === 'current').length,
    lapsed:  skb.filter(b => b.status === 'lapsed').length,
    never:   skb.filter(b => b.status === 'never').length,
  } : null;

  // Last flight
  const lastFlight = flights[0];

  // Tile opacity
  const tileOpacity = (id) => openTile !== null && openTile !== id ? 0.35 : 1;

  const tileStyle = (id, accentColor) => ({
    background: 'var(--bg-card)',
    borderRadius: 16,
    border: '1px solid var(--border)',
    borderLeft: accentColor ? `4px solid ${accentColor}` : '1px solid var(--border)',
    overflow: 'hidden',
    marginBottom: 9,
    opacity: tileOpacity(id),
    transition: 'opacity 0.25s ease',
  });

  const tileHeadStyle = (accentColor) => ({
    padding: '14px 16px',
    display: 'flex', alignItems: 'flex-start', gap: 14,
    cursor: 'pointer',
  });

  return (
    <div style={{ padding: '0 14px 16px' }}>
      <StudentBanner />

      {/* ── Hero ── */}
      <div style={{ padding: '8px 2px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: 'var(--page-bg-title)', margin: 0 }}>
            {displayName}'s Dashboard
          </h2>
          {myStudents.length > 0 && !viewingStudent && (
            <button onClick={() => setShowStudentPicker(!showStudentPicker)}
              style={{ width: 24, height: 24, borderRadius: '50%', background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', color: '#fff', border: 'none', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              👤
            </button>
          )}
        </div>
        <p style={{ fontSize: 15, color: 'var(--page-bg-sub)', margin: 0 }}>{stats.total_flights} flights claimed</p>
      </div>

      {showStudentPicker && <StudentPicker onClose={() => setShowStudentPicker(false)} />}

      {/* ── PROFICIENCY TILE ── */}
      {pl && (
        <div style={tileStyle('prof', profColor)}>
          <div style={tileHeadStyle(profColor)} onClick={() => toggleTile('prof')}>
            <ScoreBadge value={`${pl.composite}%`} label={`L${pl.level}`} score={profScore} />
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Label with left color bar */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 1 }}>
                <div style={{ width: 3, borderRadius: 2, background: profColor, alignSelf: 'stretch', minHeight: 18, flexShrink: 0 }} />
                <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Overall Proficiency Score</span>
                <button onClick={e => { e.stopPropagation(); setShowProfExplainer(true); }}
                  style={{ width: 15, height: 15, borderRadius: '50%', border: '1.5px solid var(--text-muted)', background: 'transparent', color: 'var(--text-muted)', fontSize: 9, fontWeight: 800, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>?</button>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 7 }}>{pl.label}</div>
              {/* Inline bucket bars — hide when expanded */}
              {openTile !== 'prof' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {showBucket('practical') && <InlineBucket label="Recency" value={pl.practical_score} displayVal={pl.practical_score != null ? pl.practical_score?.toFixed(0) : '\u2014'} color="var(--accent)" />}
                  {showBucket('acs') && <InlineBucket label="ACS" value={pl.acs_score} displayVal={pl.acs_score != null ? `${pl.acs_score?.toFixed(0)}%` : '—'} color="#fcd34d" />}
                  {showBucket('online') && <InlineBucket label="Online" value={pl.online_score} displayVal={pl.online_score != null ? `${pl.online_score?.toFixed(0)}%` : '—'} color="#10b981" />}
                  {showBucket('skills') && <InlineBucket label="Skills" value={pl.skills_score} displayVal={pl.skills_score != null ? pl.skills_score?.toFixed(0) : '—'} color="#f59e0b" />}
                </div>
              )}
            </div>
            <TileChevron open={openTile === 'prof'} />
          </div>

          {/* Expanded buckets */}
          {openTile === 'prof' && (
            <div style={{ borderTop: '1px solid var(--border)', padding: '11px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* Practical */}
              {showBucket('practical') && (
              <div style={{ cursor: 'pointer' }} onClick={() => navigate('/flights', { state: { feedMode: 'my_flights' } })}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>✈ Recency</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                    {pl.practical_score}
                    <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 400, marginLeft: 4 }}>wt {Math.round((pl.weights?.practical||0)*100)}%</span>
                  </span>
                </div>
                <div style={{ height: 3, borderRadius: 2, background: 'var(--bg-body)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.min(100, pl.practical_score||0)}%`, background: 'var(--accent)', opacity: 0.75, borderRadius: 2 }} />
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 3 }}>
                  Score {pl.score_quality?.toFixed(0)}% · {pl.total_flights} flights · {pl.days_since_last}d ago
                </div>
              </div>
              )}
              {/* ACS */}
              {showBucket('acs') && (
              <div style={{ cursor: 'pointer' }} onClick={() => navigate('/train?tab=acs')}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>≡ ACS Standards</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                    {pl.acs_score != null ? `${pl.acs_score?.toFixed(0)}%` : <span style={{ color: 'var(--text-muted)' }}>No data</span>}
                    {pl.acs_score != null && <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 400, marginLeft: 4 }}>wt {Math.round((pl.weights?.acs||0)*100)}%</span>}
                  </span>
                </div>
                <div style={{ height: 3, borderRadius: 2, background: 'var(--bg-body)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.min(100, pl.acs_score||0)}%`, background: '#fcd34d', opacity: 0.85, borderRadius: 2 }} />
                </div>
                {pl.acs_score != null
                  ? <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 3 }}>{pl.acs_task_done} / {pl.acs_task_total} tasks · {pl.acs_earned} / {pl.acs_total} pts</div>
                  : <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>Complete ACS items in the TGT tab</div>}
              </div>
              )}
              {/* Online */}
              {showBucket('online') && (
              <div style={{ cursor: 'pointer' }} onClick={() => navigate('/train?tab=online')}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>📖 Online</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                    {pl.online_score != null ? `${pl.online_score?.toFixed(0)}%` : <span style={{ color: 'var(--text-muted)' }}>No data</span>}
                    {pl.online_score != null && <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 400, marginLeft: 4 }}>wt {Math.round((pl.weights?.online||0)*100)}%</span>}
                  </span>
                </div>
                <div style={{ height: 3, borderRadius: 2, background: 'var(--bg-body)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.min(100, pl.online_score||0)}%`, background: '#10b981', opacity: 0.6, borderRadius: 2 }} />
                </div>
                {pl.online_score != null
                  ? <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 3 }}>{pl.online_current} / {pl.online_total} courses</div>
                  : <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>No school-assigned courses yet</div>}
              </div>
              )}
              {/* Skills */}
              {showBucket('skills') && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>🎯 Skills</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                    {pl.skills_score != null ? pl.skills_score?.toFixed(0) : <span style={{ color: 'var(--text-muted)' }}>No data</span>}
                    {pl.skills_score != null && <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 400, marginLeft: 4 }}>wt {Math.round((pl.weights?.skills||0)*100)}%</span>}
                  </span>
                </div>
                <div style={{ height: 3, borderRadius: 2, background: 'var(--bg-body)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.min(100, pl.skills_score||0)}%`, background: '#f59e0b', opacity: 0.75, borderRadius: 2 }} />
                </div>
                {pl.skills_score != null && skbCounts
                  ? <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 3 }}>{skbCounts.current} current · {skbCounts.lapsed} lapsed · {skbCounts.never} never flown</div>
                  : pl.skills_score == null ? <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>No weighted skills configured</div> : null}
              </div>
              )}
              {pl.gate_applied && (
                <div style={{ fontSize: 11, color: '#fcd34d', background: 'rgba(252,211,77,0.18)', borderRadius: 6, padding: '4px 8px' }}>
                  ⚠ {pl.gate_applied === 'min_flights' ? 'Minimum flight count not yet reached' : 'Recency gate applied'}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── LANDING SCORE + FLIGHTS TILE ── */}
      <div style={tileStyle('flights', scoreColor(pl?.practical_score))}>
        {/* Header */}
        <div style={{ ...tileHeadStyle(scoreColor(pl?.practical_score)), alignItems: 'center' }} onClick={() => toggleTile('flights')}>
          <ScoreBadge value={pl?.practical_score != null ? pl.practical_score : '--'} label="recency" score={pl?.practical_score ?? null} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 1 }}>
              Recency Score
              <button onClick={e => { e.stopPropagation(); setShowLandingExplainer(true); }}
                style={{ width: 15, height: 15, borderRadius: '50%', border: '1.5px solid var(--text-muted)', background: 'transparent', color: 'var(--text-muted)', fontSize: 9, fontWeight: 800, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginLeft: 5 }}>?</button>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
              {stats.total_flights} flights claimed
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <TileChevron open={openTile === 'flights'} />
          </div>
        </div>

        {/* Always-visible stat strip */}
        <TileDivider />
        <div style={{ display: 'flex' }}>
          <div style={{ flex: 1, padding: '11px 14px', borderRight: '1px solid var(--border)' }}>
            <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-secondary)', marginBottom: 2 }}>Last 30 days</div>
            <div style={{ fontSize: 15, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>{stats.flights_30d ?? 0}</div>
            <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 1 }}>flights</div>
          </div>
          <div style={{ flex: 1, padding: '11px 14px', borderRight: '1px solid var(--border)' }}>
            <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-secondary)', marginBottom: 2 }}>Last 60 days</div>
            <div style={{ fontSize: 15, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>{stats.flights_60d ?? 0}</div>
            <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 1 }}>flights</div>
          </div>
          <div style={{ flex: 1, padding: '11px 14px' }}>
            <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-secondary)', marginBottom: 2 }}>Last 90 days</div>
            <div style={{ fontSize: 15, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>{stats.flights_90d ?? 0}</div>
            <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 1 }}>flights</div>
          </div>
        </div>

        {/* Expanded: flight list */}
        {openTile === 'flights' && (
          <>
            <TileDivider />
            <div style={{ padding: '10px 12px 12px', display: 'flex', flexDirection: 'column', gap: 7 }}>
              {flights.length === 0 ? (
                <div className="empty-state" style={{ padding: '16px 0' }}>
                  <p style={{ fontSize: 14, color: 'var(--text-muted)' }}>No flights claimed yet</p>
                </div>
              ) : (
                <>
                  {flights.map(f => (
                    <div key={f.id}
                      onClick={() => navigate(`/flight/${f.id}`)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', background: 'var(--bg-body)', borderRadius: 10, border: '1px solid var(--border)', cursor: 'pointer' }}>
                      <div className={`grade-badge ${gradeClass(f.grade)}`} style={{ width: 36, height: 36, fontSize: 13, flexShrink: 0 }}>{f.percentage != null ? f.percentage : (f.grade || '?')}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>{f.callsign}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>
                          {f.arr_airport}{f.runway_id ? ` · RWY ${f.runway_id}` : ''} · {f.ac_type} · {formatDate(f.flight_date)}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
                        {(f.skills_json || []).slice(0, 4).map((s, i) => (
                          <span key={i} title={s.skill} style={{ background: '#334155',
                            border: '1px solid #1e293b', borderRadius: 6, padding: '1px 5px',
                            fontSize: 15, lineHeight: 1.35 }}>{skillIcon(s.skill)}</span>
                        ))}
                        {(f.skills_json || []).length > 4 && (
                          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>+{f.skills_json.length - 4}</span>
                        )}
                      </div>
                    </div>
                  ))}
                  {dashHasMore && (
                    <button onClick={loadMoreFlights} disabled={loadingMore}
                      style={{ width: '100%', padding: 10, marginTop: 4, background: 'var(--bg-card)', color: 'var(--accent)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: loadingMore ? 0.6 : 1 }}>
                      {loadingMore ? 'Loading...' : `Load More (${flights.length} of ${data.flights_total})`}
                    </button>
                  )}
                  <button onClick={() => navigate('/vfr')}
                    style={{ width: '100%', padding: 10, marginTop: 2, background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    + Add flight manually
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── SKILLS TILE ── */}
      {skills_grid && skills_grid.length > 0 && (
        <div style={tileStyle('skills', 'var(--text-muted)')}>
          {/* Header */}
          <div style={{ ...tileHeadStyle('var(--text-muted)'), alignItems: 'center' }} onClick={() => toggleTile('skills')}>
            <ScoreBadge value={pl?.skills_score != null ? Math.round(pl.skills_score) : '--'} label="skills" score={pl?.skills_score ?? null} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 1 }}>
                Skills Currency
                <button onClick={e => { e.stopPropagation(); setShowSkillsExplainer(true); }}
                  style={{ width: 15, height: 15, borderRadius: '50%', border: '1.5px solid var(--text-muted)', background: 'transparent', color: 'var(--text-muted)', fontSize: 9, fontWeight: 800, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginLeft: 5 }}>?</button>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                {skbCounts
                  ? <>{skbCounts.current} current · {skbCounts.lapsed} lapsed · {skbCounts.never} never flown</>
                  : <>{skills_grid.filter(s => s.pm_met).length} current · {skills_grid.filter(s => s.pm_value != null && !s.pm_met).length} lapsed · {skills_grid.filter(s => s.pm_value == null).length} untracked</>}
              </div>
            </div>
            <TileChevron open={openTile === 'skills'} />
          </div>

          {/* Always-visible pills */}
          <TileDivider />
          <div style={{ padding: '12px 14px 14px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {(skb.length ? skb : skills_grid).map(sg => {
              const dotColor = skb.length
                ? (sg.status === 'current' ? '#10b981' : sg.status === 'lapsed' ? '#fcd34d' : 'var(--border)')
                : (sg.pm_value == null ? 'var(--border)' : sg.pm_met ? '#10b981' : '#fcd34d');
              return (
                <div key={sg.skill} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px 7px 10px', borderRadius: 20, background: 'var(--bg-body)', border: '1px solid var(--border)', fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', opacity: skb.length && sg.status === 'never' ? 0.65 : 1 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
                  {skillIcon(sg.skill)} {sg.skill}
                </div>
              );
            })}
          </div>

          {/* Expanded: full matrix */}
          {openTile === 'skills' && (
            <>
              <TileDivider />
              <div style={{ padding: '0 14px 16px' }}>
                {!minimums && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '8px 0 4px', textAlign: 'right' }}>Set minimums in Profile →</div>
                )}
                {/* Table header */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 30px 30px 30px 48px 48px', padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>
                  {['Skill','30d','60d','90d','Max','Prof.'].map((h, i) => (
                    <div key={h} style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', textAlign: i === 0 ? 'left' : 'center' }}>{h}</div>
                  ))}
                </div>
                {/* Skill rows */}
                {skills_grid.map((sg, i) => {
                  const mp = sg.max_period;
                  const maxBg = 'rgba(234,179,8,0.12)';
                  return (
                    <div key={sg.skill} style={{ display: 'grid', gridTemplateColumns: '1fr 30px 30px 30px 48px 48px', padding: '8px 8px', borderBottom: i < skills_grid.length - 1 ? '1px solid rgba(47,61,82,0.4)' : 'none', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-primary)', overflow: 'hidden' }}>
                        <span style={{ flexShrink: 0 }}>{skillIcon(sg.skill)}</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sg.skill}</span>
                      </div>
                      {[{v: sg.d30, p: 30}, {v: sg.d60, p: 60}, {v: sg.d90, p: 90}].map(({v, p}) => (
                        <div key={p} style={{ textAlign: 'center', fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-mono)', color: v > 0 ? 'var(--text-primary)' : 'var(--text-muted)', background: mp === p ? maxBg : 'transparent', borderRadius: 4 }}>
                          {v || '—'}
                        </div>
                      ))}
                      <div style={{ textAlign: 'center', fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-mono)', color: sg.max_value ? '#eab308' : 'var(--text-muted)' }}>
                        {sg.max_value || '—'}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        {sg.pm_value != null ? (
                          <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', padding: '2px 5px', borderRadius: 6, minWidth: 32,
                            background: sg.pm_met ? 'rgba(16,185,129,0.10)' : 'rgba(245,158,11,0.10)',
                            border: sg.pm_met ? '1px solid rgba(16,185,129,0.22)' : '1px solid rgba(245,158,11,0.22)' }}>
                            <span style={{ fontSize: 11, color: sg.pm_met ? '#10b981' : '#fcd34d' }}>{sg.pm_met ? '✓' : '✗'}</span>
                            <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: sg.pm_met ? '#10b981' : '#fcd34d' }}>{sg.pm_value}</span>
                          </div>
                        ) : (
                          <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>—</span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {/* Flights summary row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 30px 30px 30px 48px 48px', padding: '8px 8px', borderTop: '2px solid var(--border)', alignItems: 'center', background: 'rgba(77,142,240,0.04)', borderRadius: '0 0 8px 8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>Σ Total</div>
                  <div style={{ textAlign: 'center', fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}>{skills_grid.reduce((s, x) => s + (x.d30 || 0), 0)}</div>
                  <div style={{ textAlign: 'center', fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}>{skills_grid.reduce((s, x) => s + (x.d60 || 0), 0)}</div>
                  <div style={{ textAlign: 'center', fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}>{skills_grid.reduce((s, x) => s + (x.d90 || 0), 0)}</div>
                  <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-muted)' }}>{skb.length && pl?.skills_score != null ? 'skills' : 'avg'}</div>
                  <div style={{ textAlign: 'center', fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-mono)', color: skb.length && pl?.skills_score != null ? '#f59e0b' : 'var(--accent)' }}>{skb.length && pl?.skills_score != null ? pl.skills_score.toFixed(0) : `${stats.avg_score}%`}</div>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', padding: '6px 8px 0', textAlign: 'center' }}>Highlighted column = most active period</div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── FAA WINGS TILE ── (gated by App Settings: show_faa_wings) */}
      {user?.app_settings?.show_faa_wings && (
        <div style={tileStyle('wings', '#0A2C5C')}>
          <div style={{ ...tileHeadStyle('#0A2C5C'), alignItems: 'center' }} onClick={() => toggleTile('wings')}>
            <div style={{ width: 50, height: 50, borderRadius: '50%', overflow: 'hidden', background: '#fff', flexShrink: 0, border: '1px solid var(--border)' }}>
              <img src="/faa-seal.jpg" alt="FAA seal" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 1 }}>
                WINGS
                <span style={{ fontSize: 9, fontWeight: 700, color: '#a16207', background: 'rgba(252,211,77,0.18)', border: '1px solid rgba(252,211,77,0.4)', padding: '1px 6px', borderRadius: 4, marginLeft: 8, verticalAlign: 1, letterSpacing: '0.04em' }}>SAMPLE</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                {MOCK_WINGS.assigned.length} assigned &middot; {MOCK_WINGS.registered.length} registered &middot; {MOCK_WINGS.completed.length} completed
              </div>
            </div>
            <TileChevron open={openTile === 'wings'} />
          </div>

          {/* Always-visible chip row */}
          <TileDivider />
          <div style={{ padding: '12px 14px 14px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 20, background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.22)', fontSize: 12, fontWeight: 600, color: '#f59e0b' }}>
              📋 {MOCK_WINGS.assigned.length} assigned
            </div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 20, background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(59,130,246,0.22)', fontSize: 12, fontWeight: 600, color: '#3b82f6' }}>
              🔖 {MOCK_WINGS.registered.length} registered
            </div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 20, background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.22)', fontSize: 12, fontWeight: 600, color: '#10b981' }}>
              ✓ {MOCK_WINGS.completed.length} completed
            </div>
          </div>

          {/* Expanded: full course lists */}
          {openTile === 'wings' && (
            <>
              <TileDivider />
              <div style={{ padding: '14px 14px 16px' }}>
                <WingsSection first title="Assigned" meta="By CFI or school" icon="📋" iconColor="#f59e0b" items={MOCK_WINGS.assigned} />
                <WingsSection title="Registered" meta="Self-enrolled" icon="🔖" iconColor="#3b82f6" items={MOCK_WINGS.registered} />
                <WingsSection title="Completed" meta="Last 12 months" icon="✓" iconColor="#10b981" items={MOCK_WINGS.completed} />
                <div style={{ marginTop: 14, padding: '10px 12px', borderRadius: 8, background: 'rgba(252,211,77,0.08)', border: '1px solid rgba(252,211,77,0.25)', fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.45 }}>
                  Showing sample data. Connect your FAASafety.gov account in <em>Profile &rarr; App Settings &rarr; FAA WINGS</em> to see your actual progress (sync coming soon).
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── EXPLAINER SHEETS ── */}
      <ExplainerSheet open={showProfExplainer} onClose={() => setShowProfExplainer(false)} title="⭐ Proficiency Level Explained">
        <div style={{ fontSize: 15, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
          Your proficiency level is a <strong style={{ color: 'var(--text-primary)' }}>composite score</strong> across weighted buckets: how well you fly, how current your skills are, how current your ACS standards are, and your online training completions.
        </div>
        <div style={{ background: 'var(--bg-body)', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[['✈','Recency (flight scores)','Based on your approach scores, flight volume, and how recently you flew. Uses a decay-weighted average of your last 10 scored approaches.'],
            ['🎯','Skills (currency)','Each weighted skill scores 100 if flown in the last 90 days, partial credit if flown longer ago, and 0 if never flown. The same statuses drive the dots on your Skills Currency tile. Approach scores are not used here.'],
            ['≡','ACS Standards','Percentage of your applicable ACS standards marked complete within their recurrency window. Scoped to your certificates, ratings, and aircraft type.'],
            ['📖','Online Training','Completion rate of courses assigned by your school. If no courses are assigned, this bucket is excluded and weight redistributed.']
          ].map(([icon, title, desc]) => (
            <div key={title} style={{ display: 'flex', gap: 10 }}>
              <span style={{ fontSize: 20, flexShrink: 0 }}>{icon}</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 3 }}>{title}</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ background: 'var(--bg-body)', borderRadius: 12, padding: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Levels</div>
          {[['L1','Establishing','#4a90d9','< 30'],['L2','Developing','#e6a817','30–54'],['L3','Proficient','#27ae60','55–74'],['L4','Sharp','#8e44ad','75–89'],['L5','Top Gun','#f39c12','90+']].map(([lv, name, color, range]) => (
            <div key={lv} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: color+'22', border: `1.5px solid ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ fontSize: 10, fontWeight: 800, color }}>{lv}</span>
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', flex: 1 }}>{name}</span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{range}</span>
            </div>
          ))}
        </div>
        <div style={{ background: 'rgba(59,130,246,0.08)', borderRadius: 12, padding: 12, border: '1px solid rgba(59,130,246,0.2)' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', marginBottom: 4 }}>Gates</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>Fewer than 3 flights forces L1 regardless of score. No flights in 180+ days caps you at L2.</div>
        </div>
      </ExplainerSheet>

      <ExplainerSheet open={showLandingExplainer} onClose={() => setShowLandingExplainer(false)} title="🛬 Recency Score Explained">
        <div style={{ fontSize: 15, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
          Your recency score reflects <strong style={{ color: 'var(--text-primary)' }}>how much and how recently you've been flying</strong> — built from your claimed flights, weighted toward the most recent.
        </div>
        <div style={{ background: 'var(--bg-body)', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[['📅','30 / 60 / 90 Days','How many claimed flights you logged in each rolling window. Consistent flying keeps skills sharp — and keeps this score up.'],
            ['⏳','Decay Weighting','Recent flights count more than older ones. A long gap since your last flight pulls the score down.']
          ].map(([icon, title, desc]) => (
            <div key={title} style={{ display: 'flex', gap: 10 }}>
              <span style={{ fontSize: 20, flexShrink: 0 }}>{icon}</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 3 }}>{title}</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ background: 'rgba(59,130,246,0.08)', borderRadius: 12, padding: 12, border: '1px solid rgba(59,130,246,0.2)' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', marginBottom: 4 }}>Scoring categories</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>Each approach is scored across Speed Control, Stabilization, Vertical Path, Horizontal Path, and Bank Angle. Tap any flight for the full breakdown.</div>
        </div>
      </ExplainerSheet>

      <ExplainerSheet open={showSkillsExplainer} onClose={() => setShowSkillsExplainer(false)} title="⚙️ Skills Currency Explained">
        <div style={{ fontSize: 15, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
          Skills Currency tracks how recently and frequently you've demonstrated <strong style={{ color: 'var(--text-primary)' }}>specific approach skills</strong>. Rolling 90-day window, not just pass/fail.
        </div>
        <div style={{ background: 'var(--bg-body)', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[['📅','30 / 60 / 90 day columns','How many times you\'ve demonstrated that skill. The highlighted column is your most active recent period.'],
            ['📊','Max column','Your best performance value — tightest crosswind, lowest ceiling approached, etc.'],
            ['🎯','Prof. (Proficiency) column','✓ green = hit your target frequency. ✗ amber = below it.']
          ].map(([icon, title, desc]) => (
            <div key={title} style={{ display: 'flex', gap: 10 }}>
              <span style={{ fontSize: 20, flexShrink: 0 }}>{icon}</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 3 }}>{title}</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ background: 'rgba(59,130,246,0.08)', borderRadius: 12, padding: 12, border: '1px solid rgba(59,130,246,0.2)' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', marginBottom: 4 }}>How skills are detected</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>Automatically extracted from approach data — crosswind, night, ceiling, IFR/VFR. No manual logging needed.</div>
        </div>
      </ExplainerSheet>
    </div>
  );
}

function _skill_style_lookup(name) {
  const map = { 'Crosswind': '🌬️', 'Gust': '💨', 'Windshear': '⚡', 'Night Flight': '🌙', 'IFR': '⛅', 'LIFR': '🔴', 'Short Field': '🛬', 'High Altitude Ops': '🏔️' };
  return [map[name] || '🎯'];
}
function _skill_icon_for(name) { return _skill_style_lookup(name)[0]; }

export { DashboardPage };
export default DashboardPage;
